import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, GitCompare } from "lucide-react";
import FunderShell from "./FunderShell";
import { RemitPill, StatusBadge, StageLabel, composite, scoreTone } from "./FunderUI";
import { getProposals, getClusters, getReviewerNames } from "../../service/FunderPortalService";

const PAGE_SIZE = 10;

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter((n) => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

const STATUS_ORDER = { Flagged: 0, "In Progress": 1, Unreviewed: 2, Scored: 3 };

function genericSummary(p) {
    return `${p.title} (${p.pi}, ${p.institution}). DeepThought situates this submission within "${p.cluster}", scoring Novelty ${p.novelty}, Methods ${p.methodology}, and Impact ${p.impact}. Second-pass reviewer analysis is pending.`;
}

export default function FunderSubmissions() {
    return (
        <FunderShell activePage="Submissions">
            {(callId, call) => <SubmissionsInner callId={callId} call={call} />}
        </FunderShell>
    );
}

function SubmissionsInner({ callId, call }) {
    const navigate = useNavigate();
    const proposals = getProposals(callId);
    const clusters = getClusters(callId);
    const reviewers = getReviewerNames(callId);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [sortKey, setSortKey] = useState("composite");
    const [sortDir, setSortDir] = useState("desc");
    const [scoreFilter, setScoreFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [remitFilter, setRemitFilter] = useState("");
    const [clusterFilter, setClusterFilter] = useState("");
    const [reviewerFilter, setReviewerFilter] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [compare, setCompare] = useState([]);

    useEffect(() => {
        setPage(1);
    }, [callId, search, scoreFilter, statusFilter, remitFilter, clusterFilter, reviewerFilter]);

    function toggleSort(key) {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(["title", "institution", "assigned"].includes(key) ? "asc" : "desc");
        }
    }

    function toggleCompare(id) {
        setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
    }

    const visible = useMemo(() => {
        let list = [...proposals];
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(
                (p) =>
                    (p.title ?? "").toLowerCase().includes(q) ||
                    (p.pi ?? "").toLowerCase().includes(q) ||
                    (p.institution ?? "").toLowerCase().includes(q)
            );
        }
        if (scoreFilter) {
            list = list.filter((p) => {
                const c = composite(p);
                if (scoreFilter === "80+") return c >= 80;
                if (scoreFilter === "70-79") return c >= 70 && c < 80;
                if (scoreFilter === "60-69") return c >= 60 && c < 70;
                if (scoreFilter === "<60") return c < 60;
                return true;
            });
        }
        if (statusFilter) {
            list = statusFilter === "Frontier" ? list.filter((p) => p.frontier) : list.filter((p) => p.status === statusFilter);
        }
        if (remitFilter) list = list.filter((p) => p.remit?.state === remitFilter);
        if (clusterFilter) list = list.filter((p) => p.cluster === clusterFilter);
        if (reviewerFilter) list = list.filter((p) => p.assigned === reviewerFilter);

        const dir = sortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            let av;
            let bv;
            if (sortKey === "composite") { av = composite(a); bv = composite(b); }
            else if (sortKey === "status") { av = STATUS_ORDER[a.status] ?? 9; bv = STATUS_ORDER[b.status] ?? 9; }
            else if (["novelty", "methodology", "impact", "id"].includes(sortKey)) { av = a[sortKey]; bv = b[sortKey]; }
            else { av = (a[sortKey] ?? "").toString().toLowerCase(); bv = (b[sortKey] ?? "").toString().toLowerCase(); }
            if (typeof av === "string") return av.localeCompare(bv) * dir;
            return (av - bv) * dir;
        });
        return list;
    }, [proposals, search, scoreFilter, statusFilter, remitFilter, clusterFilter, reviewerFilter, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const activeFilters = scoreFilter || statusFilter || remitFilter || clusterFilter || reviewerFilter || search;
    const compareReady = compare.length >= 2 && compare.length <= 4;

    const sortIcon = (key) =>
        sortKey === key ? (sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null;

    const Th = ({ label, k, center }) => (
        <th
            className="funder-th-sort"
            onClick={() => toggleSort(k)}
            style={{ textAlign: center ? "center" : "left" }}
        >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, justifyContent: center ? "center" : "flex-start" }}>
                {label}
                {sortIcon(k)}
            </span>
        </th>
    );

    const scoreCell = (p, dim) => {
        const v = p[dim];
        const band = p.conf?.[dim] ?? 0;
        return (
            <td style={{ textAlign: "center" }} title={`${v} (${Math.max(0, v - band)}-${Math.min(100, v + band)})`}>
                <span style={{ color: scoreTone(v), fontWeight: 600 }}>{v}</span>
            </td>
        );
    };

    return (
        <div className="funder-section-stack">
            <div>
                <StageLabel>Stages 2–3 · Scoring &amp; shortlisting</StageLabel>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginTop: 6 }}>
                    <h1 className="funder-h1" style={{ color: "var(--ink-900)", margin: 0 }}>Submissions</h1>
                    <span style={{ color: "var(--ink-400)", fontSize: 13 }}>{call.totalSubmissions} proposals</span>
                </div>
            </div>

            <div className="funder-card">
                <div className="funder-card-body">
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
                        <div className="funder-filters">
                            <select className="funder-select" value={scoreFilter} onChange={(e) => setScoreFilter(e.target.value)} aria-label="Score filter">
                                <option value="">All scores</option>
                                <option value="80+">80+</option>
                                <option value="70-79">70 – 79</option>
                                <option value="60-69">60 – 69</option>
                                <option value="<60">Below 60</option>
                            </select>
                            <select className="funder-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter">
                                <option value="">All statuses</option>
                                <option value="Scored">Scored</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Unreviewed">Unreviewed</option>
                                <option value="Flagged">Flagged</option>
                                <option value="Frontier">Frontier proposals</option>
                            </select>
                            <select className="funder-select" value={remitFilter} onChange={(e) => setRemitFilter(e.target.value)} aria-label="Scope filter">
                                <option value="">All scope</option>
                                <option value="in">In scope</option>
                                <option value="edge">Edge</option>
                                <option value="out">Out of scope</option>
                            </select>
                            <select className="funder-select" value={clusterFilter} onChange={(e) => setClusterFilter(e.target.value)} aria-label="Cluster filter">
                                <option value="">All clusters</option>
                                {clusters.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <select className="funder-select" value={reviewerFilter} onChange={(e) => setReviewerFilter(e.target.value)} aria-label="Reviewer filter">
                                <option value="">All reviewers</option>
                                {reviewers.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {activeFilters ? (
                                <button
                                    className="funder-link"
                                    style={{ background: "none", border: "none", padding: 0 }}
                                    onClick={() => { setSearch(""); setScoreFilter(""); setStatusFilter(""); setRemitFilter(""); setClusterFilter(""); setReviewerFilter(""); }}
                                >
                                    Clear filters
                                </button>
                            ) : null}
                        </div>

                        <div style={{ position: "relative", marginLeft: "auto" }}>
                            <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                            <input
                                type="text"
                                placeholder="Search title, PI, institution…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", minWidth: 230 }}
                            />
                            {search ? (
                                <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", cursor: "pointer", color: "var(--ink-350)", fontSize: 14 }}>×</button>
                            ) : null}
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table className="funder-table">
                            <thead>
                                <tr>
                                    <Th label="Grant #" k="id" />
                                    <Th label="Title" k="title" />
                                    <th>Scope</th>
                                    <Th label="DT Score" k="composite" center />
                                    <Th label="Institution" k="institution" />
                                    <Th label="Novelty" k="novelty" center />
                                    <Th label="Methods" k="methodology" center />
                                    <Th label="Impact" k="impact" center />
                                    <Th label="Status" k="status" />
                                    <Th label="Assigned" k="assigned" />
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((p) => {
                                    const isOpen = expandedId === p.id;
                                    return (
                                        <React.Fragment key={p.id}>
                                            <tr
                                                className={`funder-row-clickable${isOpen ? " funder-row-expanded" : ""}`}
                                                onClick={() => setExpandedId(isOpen ? null : p.id)}
                                            >
                                                <td className="funder-grant-no">{p.grantNumber}</td>
                                                <td className="funder-title-cell">
                                                    <span
                                                        className="funder-link"
                                                        style={{ color: "var(--ink-900)", fontWeight: 500 }}
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/funder/proposal?id=${p.id}`); }}
                                                    >
                                                        {p.title}
                                                    </span>
                                                    {p.frontier ? <span className="funder-frontier-pill" style={{ marginLeft: 8 }} title="Top 15% Novelty + Impact">Frontier</span> : null}
                                                </td>
                                                <td><RemitPill remit={p.remit} /></td>
                                                <td style={{ textAlign: "center" }}><span className="funder-dt-chip">{composite(p)}</span></td>
                                                <td style={{ color: "var(--ink-500)" }}>{p.institution}</td>
                                                {scoreCell(p, "novelty")}
                                                {scoreCell(p, "methodology")}
                                                {scoreCell(p, "impact")}
                                                <td><StatusBadge status={p.status} /></td>
                                                <td style={{ color: "var(--ink-500)", whiteSpace: "nowrap" }}>{p.assigned ?? "—"}</td>
                                                <td style={{ textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                                                    <span className="funder-link" onClick={() => navigate(`/funder/proposal?id=${p.id}`)}>View</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={compare.includes(p.id)}
                                                        onChange={() => toggleCompare(p.id)}
                                                        aria-label={`Add ${p.grantNumber} to comparison`}
                                                        style={{ marginLeft: 10, cursor: "pointer" }}
                                                    />
                                                </td>
                                            </tr>
                                            {isOpen ? (
                                                <tr className="funder-row-expanded">
                                                    <td colSpan={11}>
                                                        <div style={{ padding: "4px 4px 8px" }}>
                                                            <p style={{ fontSize: 13, color: "var(--ink-700)", lineHeight: 1.5, margin: "0 0 10px" }}>
                                                                {p.summary?.[0] ?? genericSummary(p)}
                                                            </p>
                                                            <div className="funder-expand-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                                                <div>
                                                                    <div className="funder-kicker">Strengths</div>
                                                                    {(p.strengths ?? []).slice(0, 2).map((s, i) => (
                                                                        <div key={i} style={{ fontSize: 12.5, color: "var(--ink-700)", marginTop: 4 }}>
                                                                            <span style={{ color: "var(--success)", fontWeight: 700 }}>+ </span>{s.title} — {s.description}
                                                                        </div>
                                                                    ))}
                                                                    {!p.strengths?.length ? <div style={{ fontSize: 12.5, color: "var(--ink-350)", marginTop: 4 }}>Pending second-pass review.</div> : null}
                                                                </div>
                                                                <div>
                                                                    <div className="funder-kicker">Concern</div>
                                                                    {(p.concerns ?? []).slice(0, 1).map((c, i) => (
                                                                        <div key={i} style={{ fontSize: 12.5, color: "var(--ink-700)", marginTop: 4 }}>
                                                                            <span style={{ color: "var(--series-amber)", fontWeight: 700 }}>! </span>{c.title} — {c.description}
                                                                        </div>
                                                                    ))}
                                                                    {!p.concerns?.length ? <div style={{ fontSize: 12.5, color: "var(--ink-350)", marginTop: 4 }}>None recorded.</div> : null}
                                                                </div>
                                                            </div>
                                                            <div style={{ marginTop: 10 }}>
                                                                <span className="funder-link" onClick={() => navigate(`/funder/proposal?id=${p.id}`)}>Open full detail →</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                                {!paginated.length ? (
                                    <tr><td colSpan={11}><div className="funder-empty">No submissions match these filters.</div></td></tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>

                    {visible.length > 0 ? (
                        <div className="pagination-bar" style={{ marginTop: 14 }}>
                            <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, visible.length)} of {visible.length}</p>
                            <div className="pagination-controls">
                                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4 }}><ChevronLeft size={13} />Prev</button>
                                <div className="pagination-pages">
                                    {pageWindows(safePage, totalPages).map((n, i) =>
                                        n === null
                                            ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                            : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                    )}
                                </div>
                                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4 }}>Next<ChevronRight size={13} /></button>
                            </div>
                            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                                {compare.length > 0 ? (
                                    <span style={{ fontSize: 12, color: compareReady ? "var(--info)" : "var(--ink-350)" }}>
                                        <GitCompare size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                                        {compareReady ? "Comparison coming soon" : `Select 2–4 to compare (${compare.length})`}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    <p style={{ fontSize: 11.5, color: "var(--ink-350)", marginTop: 12, lineHeight: 1.5 }}>
                        Scope is computed by comparing each proposal against previously funded Discovery research. Close matches are marked <strong>in scope</strong>; others are flagged <strong>edge</strong> or <strong>out of scope</strong> for human review. DeepThought scores are directional signals — every proposal still receives expert review.
                    </p>
                </div>
            </div>
        </div>
    );
}
